import { useRef } from 'react'

import { PlusCircleFilled, RightOutlined } from '@ant-design/icons'
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/react/LexicalHorizontalRuleNode'
import { Col, Dropdown, Row } from 'antd'
import { createStyles } from 'antd-style'
import { noop } from 'lodash'

import type { GenericalRecord } from '@eastcoal/common/types/utilities'

import { NeAttachmentIcon } from '@eastcoal/icon/NeAttachmentIcon'
import { NullIcon } from '@eastcoal/icon/NullIcon'
import { WpsDividerIcon } from '@eastcoal/icon/WpsDividerIcon'
import { WpsEmojiIcon } from '@eastcoal/icon/WpsEmojiIcon'
import { WpsImageIcon } from '@eastcoal/icon/WpsImageIcon'
import { WpsLinkIcon } from '@eastcoal/icon/WpsLinkIcon'
import { WpsQuotaIcon } from '@eastcoal/icon/WpsQuotaIcon'
import { WpsTableIcon } from '@eastcoal/icon/WpsTableIcon'

import { useEditor } from '../../hooks/useEditor'
import { UPLOAD_ATTACHMENT_COMMAND } from '../../plugins/AttachmentPlugin/commands'
import { INSERT_EMOJI_COMMAND } from '../../plugins/EmojiPlugin/commands'
import { UPLOAD_IMAGE_COMMAND } from '../../plugins/ImagePlugin/commands'
import { INSERT_LINK_COMMAND } from '../../plugins/LinkPlugin/commands'
import { INSERT_QUOTE_COMMAND } from '../../plugins/QuotePlugin/commands'
import { SHORTCUTS } from '../../plugins/ShortcutsPlugin/shortcuts'
import { EmojiPanel } from '../../ui/EmojiPanel'
import { type EmojiObject } from '../../ui/EmojiPanel/interface'
import { MenuButton } from '../../ui/MenuButton'
import { TableCellPanel } from '../../ui/TableCellPanel'
import { type TableCellPanelRef } from '../../ui/TableCellPanel/interface'
import { $insertTable } from '../../utils/table'

const useStyles = createStyles(({ prefixCls, token }) => ({
  insertIcon: {
    color: token.cyan
  },
  menuOverride: {
    fontSize: 'red',
    [`& .${prefixCls}-dropdown-menu-sub, .${prefixCls}-dropdown-menu-item, .${prefixCls}-dropdown-menu-submenu-title`]:
      {
        padding: `0 !important`,
        [`&:hover`]: {
          backgroundColor: 'initial !important'
        }
      }
  },
  menu: {
    width: 296
  },
  menuItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: token.paddingSM,
    borderRadius: token.borderRadius,
    [`&:hover`]: {
      backgroundColor: token.controlItemBgHover
    }
  },
  menuItemContent: {
    flex: 1,
    display: 'flex',
    alignItems: 'center'
  },
  menuItemIcon: {
    flex: 'none',
    width: 32,
    height: 32,
    lineHeight: '32px',
    textAlign: 'center',
    marginRight: token.marginSM,
    fontSize: 16,
    borderRadius: token.borderRadiusXS,
    backgroundColor: token.colorWhite,
    boxShadow: `0 1px 4px rgba(24,26,31,.12)`
  },
  menuItemLabel: {
    flex: 1
  },
  menuItemArrow: {
    fontSize: 12,
    color: token.colorIcon
  }
}))

const MenuItem = ({
  className,
  label,
  icon,
  arrow,
  // Dropdown 传递的 props
  ...restProps
}: GenericalRecord) => {
  const { cx, styles } = useStyles()

  return (
    <div
      className={cx(styles.menuItem, className)}
      role='button'
      tabIndex={-1}
      {...restProps}
    >
      <span className={styles.menuItemContent}>
        <span className={styles.menuItemIcon}>{icon}</span>
        <span className={styles.menuItemLabel}>{label}</span>
      </span>
      {arrow && <RightOutlined className={styles.menuItemArrow} />}
    </div>
  )
}

export const InsertDropdown = () => {
  const editor = useEditor()
  const { cx, styles } = useStyles()

  const handleUploadImage = () => {
    editor.dispatchCommand(UPLOAD_IMAGE_COMMAND, undefined)
  }

  const handleUploadAttachment = () => {
    editor.dispatchCommand(UPLOAD_ATTACHMENT_COMMAND, undefined)
  }

  const handleInsertTable = (rows: number, cols: number) => {
    editor.update(() => {
      $insertTable(editor, {
        columns: `${cols}`,
        rows: `${rows}`
      })
    })
  }

  const handleInsertUrl = () => {
    editor.dispatchCommand(INSERT_LINK_COMMAND, undefined)
  }

  const handleInsertQuota = () => {
    editor.dispatchCommand(INSERT_QUOTE_COMMAND, undefined)
  }

  const handleInsertDivider = () => {
    editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined)
  }

  const handleInsertEmoji = (value: EmojiObject) => {
    editor.dispatchCommand(INSERT_EMOJI_COMMAND, value)
  }

  const tableCellPanelRef = useRef<TableCellPanelRef>(null)

  return (
    <Dropdown
      arrow={false}
      placement='bottomRight'
      trigger={['click']}
      menu={{
        className: cx(styles.menu, styles.menuOverride),
        selectable: false,
        expandIcon: <NullIcon />,
        items: [
          {
            key: 'basicGroup',
            label: '基础',
            type: 'group',
            children: [
              {
                key: 'image',
                label: (
                  <Row>
                    <Col span={12}>
                      <MenuItem
                        icon={<WpsImageIcon />}
                        label='图片'
                        onClick={handleUploadImage}
                      />
                    </Col>
                    <Col span={12}>
                      <MenuItem
                        icon={<NeAttachmentIcon />}
                        label='附件'
                        onClick={handleUploadAttachment}
                      />
                    </Col>
                  </Row>
                )
              },
              {
                key: 'table',
                label: <MenuItem arrow icon={<WpsTableIcon />} label='表格' />,
                popupClassName: styles.menuOverride,
                onMouseLeave: () => {
                  setTimeout(tableCellPanelRef.current?.reset ?? noop, 200)
                },
                children: [
                  {
                    key: 'cellPanel',
                    label: (
                      <TableCellPanel
                        ref={tableCellPanelRef}
                        cols={10}
                        rows={8}
                        title='插入表格'
                        onSelect={handleInsertTable}
                      />
                    )
                  }
                ]
              }
            ]
          },
          {
            key: 'generalGroup',
            label: '通用',
            type: 'group',
            children: [
              {
                key: 'link',
                label: (
                  <MenuItem
                    icon={<WpsLinkIcon />}
                    label={`链接（${SHORTCUTS.INSERT_LINK}）`}
                    onClick={handleInsertUrl}
                  />
                )
              },
              {
                key: 'qouta',
                label: (
                  <MenuItem
                    icon={<WpsQuotaIcon />}
                    label={`引用（${SHORTCUTS.QUOTE}）`}
                    onClick={handleInsertQuota}
                  />
                )
              },
              {
                key: 'divider',
                label: (
                  <MenuItem
                    icon={<WpsDividerIcon />}
                    label={`分割线（${SHORTCUTS.HORIZONTAL_RULE}）`}
                    onClick={handleInsertDivider}
                  />
                )
              },
              {
                key: 'emoji',
                label: (
                  <MenuItem arrow icon={<WpsEmojiIcon />} label='表情符号' />
                ),
                popupClassName: styles.menuOverride,
                children: [
                  {
                    key: 'emojiPanel',
                    label: <EmojiPanel onSelect={handleInsertEmoji} />
                  }
                ]
              }
            ]
          }
        ]
      }}
    >
      <MenuButton icon={<PlusCircleFilled className={styles.insertIcon} />}>
        插入
      </MenuButton>
    </Dropdown>
  )
}
